// Order tracking page: sends the order number and email to the tracking Worker and shows the order status,
// parcels with carrier tracking, and the items. Labels come from the section's translated JSON.
if (!customElements.get('workspace-order-tracking')) {
  const PARCEL_FROM_17TRACK = {
    NotFound: 'awaiting',
    InfoReceived: 'info_received',
    InTransit: 'in_transit',
    OutForDelivery: 'out_for_delivery',
    AvailableForPickup: 'pickup',
    DeliveryFailure: 'attempted',
    Delivered: 'delivered',
    Exception: 'problem',
    Expired: 'no_updates',
  };
  const PARCEL_FROM_SHOPIFY = {
    CONFIRMED: 'info_received',
    LABEL_PRINTED: 'info_received',
    LABEL_PURCHASED: 'info_received',
    IN_TRANSIT: 'in_transit',
    OUT_FOR_DELIVERY: 'out_for_delivery',
    READY_FOR_PICKUP: 'pickup',
    ATTEMPTED_DELIVERY: 'attempted',
    DELIVERED: 'delivered',
    PICKED_UP: 'delivered',
    FAILURE: 'problem',
    NOT_DELIVERED: 'problem',
    DELAYED: 'delayed',
    LABEL_VOIDED: 'problem',
    CANCELED: 'problem',
    CARRIER_PICKED_UP: 'in_transit',
    FULFILLED: 'shipped',
    MARKED_AS_FULFILLED: 'shipped',
    SUBMITTED: 'awaiting',
  };
  const PAID = ['PAID', 'PARTIALLY_PAID', 'PARTIALLY_REFUNDED', 'AUTHORIZED'];
  const VISIBLE_EVENTS = 5;

  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  };
  const fill = (template, values) => template.replace(/__TRACK_(\w+)__/g, (_, key) => values[key] ?? '');
  const safeUrl = (value) => {
    try {
      const url = new URL(value);
      return ['https:', 'http:'].includes(url.protocol) ? url.href : '';
    } catch {
      return '';
    }
  };

  customElements.define(
    'workspace-order-tracking',
    class WorkspaceOrderTracking extends HTMLElement {
      connectedCallback() {
        if (this.form) return;
        this.t = JSON.parse(this.querySelector('[data-track-i18n]').textContent);
        this.form = this.querySelector('[data-track-form]');
        this.button = this.querySelector('[data-track-submit]');
        this.message = this.querySelector('[data-track-message]');
        this.result = this.querySelector('[data-track-result]');
        this.locale = this.dataset.locale || document.documentElement.lang || 'en';
        this.buttonLabel = this.button.textContent;
        const order = new URLSearchParams(window.location.search).get('order');
        if (order) this.form.elements.order.value = order;
        this.onSubmit = (event) => {
          event.preventDefault();
          this.lookup();
        };
        this.form.addEventListener('submit', this.onSubmit);
      }

      disconnectedCallback() {
        this.controller?.abort();
      }

      async lookup() {
        if (!this.form.reportValidity() || this.button.getAttribute('aria-busy') === 'true') return;
        this.button.setAttribute('aria-busy', 'true');
        this.button.disabled = true;
        this.button.textContent = this.t.loading;
        this.showMessage('');
        this.result.hidden = true;
        this.result.replaceChildren();
        this.controller = new AbortController();
        const timeout = window.setTimeout(() => this.controller.abort(), 30000);
        try {
          const response = await fetch(this.dataset.endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ order: this.form.elements.order.value, email: this.form.elements.email.value }),
            signal: this.controller.signal,
          });
          const data = await response.json().catch(() => ({ error: 'unavailable' }));
          if (!response.ok || data.error) {
            this.result.hidden = true;
            this.showMessage(this.t.errors[data.error] || this.t.errors.unavailable);
            return;
          }
          this.render(data);
        } catch (error) {
          this.showMessage(this.t.errors.unavailable);
        } finally {
          window.clearTimeout(timeout);
          this.button.removeAttribute('aria-busy');
          this.button.disabled = false;
          this.button.textContent = this.buttonLabel;
        }
      }

      showMessage(text) {
        this.message.textContent = text;
        this.message.hidden = !text;
      }

      date(value, options = { day: 'numeric', month: 'long', year: 'numeric' }) {
        if (!value) return '';
        const date = new Date(value);
        if (!Number.isFinite(date.getTime())) return '';
        return new Intl.DateTimeFormat(this.locale, options).format(date);
      }

      parcelStatus(shipment) {
        const shopify = PARCEL_FROM_SHOPIFY[shipment.status];
        if (shipment.deliveredAt || shopify === 'delivered') return 'delivered';
        if (shipment.live?.status === 'NotFound') return shopify || 'awaiting';
        return PARCEL_FROM_17TRACK[shipment.live?.status] || shopify || 'no_updates';
      }

      orderState({ order, shipments }) {
        if (order.cancelled) return { key: 'cancelled', step: -1 };
        const parcels = shipments.map((s) => this.parcelStatus(s));
        const allShipped = order.fulfillment === 'FULFILLED';
        if (shipments.length && allShipped && parcels.every((p) => p === 'delivered')) return { key: 'delivered', step: 3 };
        if (parcels.includes('problem')) return { key: 'problem', step: 2 };
        if (parcels.includes('attempted')) return { key: 'attempted', step: 2 };
        if (parcels.includes('delayed')) return { key: 'delayed', step: 2 };
        if (parcels.includes('pickup')) return { key: 'pickup', step: 2 };
        if (parcels.includes('out_for_delivery')) return { key: 'out_for_delivery', step: 2 };
        if (shipments.length) return { key: allShipped ? 'shipped' : 'partly_shipped', step: 2 };
        if (allShipped || order.fulfillment === 'PARTIALLY_FULFILLED') return { key: allShipped ? 'shipped' : 'partly_shipped', step: 2 };
        if (PAID.includes(order.payment)) return { key: 'preparing', step: 1 };
        return { key: 'ordered', step: 0 };
      }

      render(data) {
        const { order, shipments } = data;
        const state = this.orderState(data);
        const cards = [this.summaryCard(order, shipments, state)];
        shipments.forEach((shipment, index) => cards.push(this.parcelCard(shipment, index, shipments.length)));
        cards.push(this.itemsCard(order.items));
        const reset = el('button', 'ws-btn ws-btn--outline', this.t.new_search);
        reset.type = 'button';
        reset.addEventListener('click', () => {
          this.result.hidden = true;
          this.result.replaceChildren();
          this.form.elements.order.value = '';
          this.form.elements.order.focus();
        });
        cards.push(reset);
        this.result.replaceChildren(...cards);
        this.result.hidden = false;
        this.result.focus({ preventScroll: true });
        this.result.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
      }

      summaryCard(order, shipments, state) {
        const card = el('section', 'ws-track__card');
        const head = el('div', 'ws-track__head');
        const titles = el('div');
        titles.append(el('p', 'ws-eyebrow ws-muted', this.t.order), el('h2', 'ws-h3', order.name), el('p', 'ws-muted', fill(this.t.placed, { date: this.date(order.createdAt) })));
        head.append(titles, el('span', `ws-track__pill${state.key === 'cancelled' ? ' ws-track__pill--soft' : ''}`, this.t.order_status[state.key]));
        card.append(head);
        if (state.key === 'cancelled') {
          card.append(el('p', 'ws-track__note', this.t.cancelled_note));
          return card;
        }
        const steps = el('ol', 'ws-track__steps');
        ['ordered', 'preparing', 'shipped', 'delivered'].forEach((key, index) => {
          const step = el('li', `ws-track__step${index < state.step ? ' ws-track__step--done' : index === state.step ? ' ws-track__step--current' : ''}`, this.t.steps[key]);
          if (index === state.step) step.setAttribute('aria-current', 'step');
          steps.append(step);
        });
        card.append(steps);
        if (!shipments.length) card.append(el('p', 'ws-track__note', state.step === 2 ? this.t.no_tracking_note : state.key === 'ordered' ? this.t.ordered_note : this.t.preparing_note));
        return card;
      }

      parcelCard(shipment, index, total) {
        const card = el('section', 'ws-track__card');
        const status = this.parcelStatus(shipment);
        const trackingUrl = safeUrl(shipment.url);
        const head = el('div', 'ws-track__head');
        head.append(el('h2', 'ws-h4', total > 1 ? fill(this.t.parcel, { index: index + 1, total }) : this.t.your_parcel), el('span', 'ws-track__pill ws-track__pill--soft', this.t.parcel_status[status]));
        card.append(head);

        const facts = el('dl', 'ws-track__facts');
        const fact = (label, value) => {
          if (!value) return;
          const row = el('div');
          const dd = el('dd');
          dd.append(value);
          row.append(el('dt', '', label), dd);
          facts.append(row);
        };
        fact(this.t.carrier, shipment.carrier);
        if (shipment.number) {
          const number = trackingUrl ? el('a', '', shipment.number) : document.createTextNode(shipment.number);
          if (trackingUrl) Object.assign(number, { href: trackingUrl, target: '_blank', rel: 'noopener noreferrer' });
          fact(this.t.tracking_number, number);
        }
        if (status === 'delivered') {
          const deliveryDate = shipment.deliveredAt || shipment.live?.events?.find((event) => event.status === 'Delivered')?.time;
          fact(this.t.delivered_on, this.date(deliveryDate));
        } else fact(this.t.estimated_delivery, this.eta(shipment));
        if (facts.children.length) card.append(facts);

        if (trackingUrl) {
          const actions = el('p', 'ws-track__actions');
          const link = el('a', 'ws-btn ws-btn--outline ws-btn--small', this.t.carrier_link);
          Object.assign(link, { href: trackingUrl, target: '_blank', rel: 'noopener noreferrer' });
          actions.append(link);
          card.append(actions);
        }

        const events = shipment.live?.events?.length ? shipment.live.events : shipment.events || [];
        if (events.length) {
          card.append(el('h3', 'ws-track__subtitle', this.t.updates));
          const list = el('ol', 'ws-track__events');
          events.forEach((event, i) => {
            const item = el('li', 'ws-track__event');
            item.hidden = i >= VISIBLE_EVENTS;
            const meta = [this.date(event.time, { dateStyle: 'medium', timeStyle: 'short' }), event.location].filter(Boolean).join(' · ');
            item.append(el('p', 'ws-track__event-text', event.description || this.t.parcel_status[PARCEL_FROM_17TRACK[event.status] || PARCEL_FROM_SHOPIFY[event.status] || 'no_updates']), el('p', 'ws-track__event-meta', meta));
            list.append(item);
          });
          card.append(list);
          if (events.length > VISIBLE_EVENTS) {
            const more = el('button', 'ws-btn ws-btn--outline ws-btn--small ws-track__more', fill(this.t.show_all_updates, { count: events.length }));
            more.type = 'button';
            more.addEventListener('click', () => {
              list.querySelectorAll('[hidden]').forEach((item) => (item.hidden = false));
              more.hidden = true;
            });
            card.append(more);
          }
        } else {
          card.append(el('p', 'ws-track__note', this.t.no_updates_yet));
        }

        if (total > 1) {
          card.append(el('h3', 'ws-track__subtitle', shipment.sharedItems ? this.t.shared_parcel_items : this.t.parcel_items));
          card.append(this.itemList(shipment.items, false));
        }
        return card;
      }

      eta(shipment) {
        const eta = shipment.live?.eta;
        if (eta?.from && eta?.to && this.date(eta.from) && this.date(eta.to) && new Date(eta.from) <= new Date(eta.to) && eta.from.slice(0, 10) !== eta.to.slice(0, 10)) {
          const format = new Intl.DateTimeFormat(this.locale, { day: 'numeric', month: 'long' });
          return format.formatRange ? format.formatRange(new Date(eta.from), new Date(eta.to)) : `${format.format(new Date(eta.from))} – ${format.format(new Date(eta.to))}`;
        }
        return this.date(eta?.from || eta?.to || shipment.estimatedDeliveryAt, { day: 'numeric', month: 'long' });
      }

      itemsCard(items) {
        const card = el('section', 'ws-track__card');
        card.append(el('h2', 'ws-h4', this.t.items), this.itemList(items, true));
        return card;
      }

      itemList(items, withImages) {
        const list = el('ul', 'ws-track__items');
        items.forEach((item) => {
          const row = el('li', `ws-track__item${withImages ? '' : ' ws-track__item--plain'}`);
          if (withImages) {
            const media = el('div', 'ws-track__item-media');
            const imageUrl = safeUrl(item.image);
            if (imageUrl) {
              const img = el('img');
              Object.assign(img, { src: imageUrl, alt: '', loading: 'lazy', width: 64, height: 64 });
              media.append(img);
            }
            row.append(media);
          }
          const text = el('div');
          text.append(el('p', '', item.title), el('p', '', [item.variant && item.variant !== 'Default Title' ? item.variant : '', fill(this.t.quantity, { count: item.quantity })].filter(Boolean).join(' · ')));
          row.append(text);
          list.append(row);
        });
        return list;
      }
    }
  );
}
